import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/store';
import { Plus, Trash2, Save, Image, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminShopSettingsPage() {
  const { token } = useAuthStore();
  const headers = { Authorization: `Bearer ${token}` };

  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Banner form
  const [bannerForm, setBannerForm] = useState({ image_url: '', title: '' });
  const [savingBanner, setSavingBanner] = useState(false);

  // Category image form
  const [catImageUrl, setCatImageUrl] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [savingCat, setSavingCat] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bannersRes, catsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/shop/banners`, { headers }),
        axios.get(`${API_URL}/api/categories`, { headers }),
      ]);
      setBanners(bannersRes.data);
      setCategories(catsRes.data);
    } catch (err) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // === BANNERS ===
  const addBanner = async () => {
    if (!bannerForm.image_url.trim()) return toast.error('Görsel linki gerekli');
    setSavingBanner(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/shop/banners`, {
        image_url: bannerForm.image_url.trim(),
        title: bannerForm.title.trim() || null,
        sort_order: banners.length,
      }, { headers });
      setBanners([...banners, res.data]);
      setBannerForm({ image_url: '', title: '' });
      toast.success('Banner eklendi');
    } catch (err) {
      toast.error('Banner eklenemedi');
    } finally {
      setSavingBanner(false);
    }
  };

  const deleteBanner = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/shop/banners/${id}`, { headers });
      setBanners(banners.filter(b => b.id !== id));
      toast.success('Banner silindi');
    } catch (err) {
      toast.error('Banner silinemedi');
    }
  };

  const toggleBanner = async (banner) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/shop/banners/${banner.id}`, {
        is_active: !banner.is_active,
      }, { headers });
      setBanners(banners.map(b => b.id === banner.id ? res.data : b));
    } catch (err) {
      toast.error('Güncellenemedi');
    }
  };

  // === CATEGORY IMAGES ===
  const saveCategoryImage = async (catId) => {
    if (!catImageUrl.trim()) return toast.error('Görsel linki gerekli');
    setSavingCat(true);
    try {
      const res = await axios.put(`${API_URL}/api/categories/${catId}`, {
        image_url: catImageUrl.trim(),
      }, { headers });
      setCategories(categories.map(c => c.id === catId ? { ...c, image_url: catImageUrl.trim() } : c));
      setEditingCat(null);
      setCatImageUrl('');
      toast.success('Kategori görseli güncellendi');
    } catch (err) {
      toast.error('Güncellenemedi');
    } finally {
      setSavingCat(false);
    }
  };

  const removeCategoryImage = async (catId) => {
    try {
      await axios.put(`${API_URL}/api/categories/${catId}`, { image_url: '' }, { headers });
      setCategories(categories.map(c => c.id === catId ? { ...c, image_url: '' } : c));
      toast.success('Görsel kaldırıldı');
    } catch (err) {
      toast.error('Güncellenemedi');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a224f]"></div>
      </div>
    );
  }

  const topLevelCats = categories.filter(c => !c.parent_id);

  return (
    <div className="space-y-3 sm:space-y-6" data-testid="admin-shop-settings">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Magaza Yonetimi</h1>
        <p className="text-sm text-gray-500">Bayi magazasinin banner ve kategori gorsellerini yonetin</p>
      </div>

      {/* BANNERS */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Image className="w-5 h-5 text-[#1a224f]" />
            Banner Yonetimi
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Bayi siparis sayfasinin ust kisminda gosterilecek gorseller</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Add Banner Form */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Gorsel linki (URL)"
              value={bannerForm.image_url}
              onChange={(e) => setBannerForm({ ...bannerForm, image_url: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
              data-testid="banner-url-input"
            />
            <input
              type="text"
              placeholder="Baslik (opsiyonel)"
              value={bannerForm.title}
              onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
              className="sm:w-48 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
              data-testid="banner-title-input"
            />
            <button
              onClick={addBanner}
              disabled={savingBanner || !bannerForm.image_url.trim()}
              className="px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a] disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
              data-testid="add-banner-btn"
            >
              <Plus className="w-4 h-4" />
              Banner Ekle
            </button>
          </div>

          {/* Banner List */}
          {banners.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">Henuz banner eklenmemis</div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="w-10 px-2 py-2.5 text-center text-xs font-semibold text-gray-500">#</th>
                    <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-40">Görsel</th>
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Başlık</th>
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Bağlantı</th>
                    <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-24">Durum</th>
                    <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-24">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {banners.map((banner, idx) => (
                    <tr key={banner.id} className={`border-b border-gray-100 last:border-0 transition-colors ${idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50 ${!banner.is_active && 'opacity-60'}`} data-testid={`banner-item-${idx}`}>
                      <td className="px-2 py-2 text-center text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <div className="w-32 h-16 mx-auto rounded overflow-hidden bg-gray-100">
                          <img src={banner.image_url} alt={banner.title || 'Banner'} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-900 truncate max-w-[180px]">{banner.title || 'Banner'}</p>
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-xs text-gray-500 truncate max-w-[280px]">{banner.image_url}</p>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${banner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {banner.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-0.5">
                          <button onClick={() => toggleBanner(banner)} className={`p-1.5 rounded ${banner.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={banner.is_active ? 'Gizle' : 'Göster'}>
                            {banner.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button onClick={() => deleteBanner(banner.id)} className="p-1.5 rounded text-red-400 hover:bg-[#eef0f7] hover:text-[#1a224f]" data-testid={`delete-banner-${idx}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CATEGORY IMAGES */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Image className="w-5 h-5 text-[#1a224f]" />
            Kategori Gorselleri
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Her kategoriye gorsel ekleyerek bayi magazasinda gorsel olarak gosterim saglayabilirsiniz</p>
        </div>

        <div className="p-6">
          {topLevelCats.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">Henuz kategori eklenmemis</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {topLevelCats.map((cat) => (
                <div key={cat.id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`cat-item-${cat.id}`}>
                  {/* Image preview */}
                  <div className="h-36 bg-gray-100 flex items-center justify-center overflow-hidden">
                    {cat.image_url ? (
                      <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="text-center text-gray-300">
                        <Image className="w-10 h-10 mx-auto mb-1" />
                        <p className="text-xs">Gorsel yok</p>
                      </div>
                    )}
                  </div>

                  <div className="p-3 space-y-2">
                    <p className="font-medium text-sm text-gray-900">{cat.name}</p>

                    {editingCat === cat.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Gorsel URL'si yapistirin"
                          value={catImageUrl}
                          onChange={(e) => setCatImageUrl(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                          data-testid={`cat-img-input-${cat.id}`}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveCategoryImage(cat.id)}
                            disabled={savingCat}
                            className="flex-1 px-3 py-1.5 bg-[#1a224f] text-white rounded-lg text-xs font-medium hover:bg-[#12173a] disabled:opacity-50 flex items-center justify-center gap-1"
                          >
                            <Save className="w-3 h-3" />Kaydet
                          </button>
                          <button
                            onClick={() => { setEditingCat(null); setCatImageUrl(''); }}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50"
                          >
                            İptal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setEditingCat(cat.id); setCatImageUrl(cat.image_url || ''); }}
                          className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1"
                          data-testid={`cat-edit-btn-${cat.id}`}
                        >
                          <Image className="w-3 h-3" />
                          {cat.image_url ? 'Gorseli Degistir' : 'Gorsel Ekle'}
                        </button>
                        {cat.image_url && (
                          <button
                            onClick={() => removeCategoryImage(cat.id)}
                            className="px-2 py-1.5 border border-red-200 rounded-lg text-xs text-red-500 hover:bg-[#eef0f7]"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
